import { useEffect, useRef, useState } from "react";
import { moreToolsPipeline } from "lib/moreToolsData";

function PipeArrow() {
  return (
    <div className="mt-pipe-arrow" aria-hidden>
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line
          x1="4"
          y1="20"
          x2="30"
          y2="20"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="3 3"
          opacity=".5"
        />
        <path
          d="M26 14 L34 20 L26 26"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx="34"
          cy="20"
          r="3"
          fill="color-mix(in srgb, currentColor 28%, transparent)"
          stroke="currentColor"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}

function PipelineCanvas({ kind }: { kind: (typeof moreToolsPipeline)[number]["kind"] }) {
  if (kind === "video") {
    return (
      <svg viewBox="0 0 96 84" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="14" y="16" width="68" height="40" rx="6" stroke="currentColor" strokeWidth="2.2" fill="color-mix(in srgb, currentColor 18%, transparent)" />
        <path className="mt-play-pulse" d="M40 26 L58 36 L40 46 Z" fill="currentColor" />
        <rect className="mt-list-reveal" x="14" y="64" width="68" rx="3" fill="color-mix(in srgb, currentColor 38%, transparent)" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "shield") {
    return (
      <svg viewBox="0 0 96 84" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M48 10 L78 22 V42 C78 60 64 72 48 78 C32 72 18 60 18 42 V22 Z"
          stroke="currentColor"
          strokeWidth="2.2"
          fill="color-mix(in srgb, currentColor 16%, transparent)"
        />
        <path d="M36 42 Q48 32 60 42 Q48 52 36 42 Z" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="48" cy="42" r="3.5" fill="currentColor" />
      </svg>
    );
  }
  if (kind === "send") {
    return (
      <svg viewBox="0 0 96 84" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect className="mt-phone-pulse" x="28" y="18" width="28" height="48" rx="6" fill="color-mix(in srgb, currentColor 25%, transparent)" stroke="currentColor" strokeWidth="2.2" />
        <rect x="33" y="26" width="18" height="12" rx="2" fill="color-mix(in srgb, currentColor 42%, transparent)" stroke="currentColor" strokeWidth="1.4" />
        <g className="mt-msg-burst1">
          <rect x="42" y="26" width="20" height="10" rx="3" fill="currentColor" stroke="currentColor" strokeWidth="1.2" />
        </g>
        <g className="mt-msg-burst2">
          <rect x="42" y="38" width="16" height="9" rx="3" fill="color-mix(in srgb, currentColor 88%, transparent)" stroke="currentColor" strokeWidth="1.2" />
        </g>
        <g className="mt-msg-burst3">
          <rect x="42" y="18" width="14" height="8" rx="3" fill="color-mix(in srgb, currentColor 78%, transparent)" stroke="currentColor" strokeWidth="1.2" />
        </g>
        <g className="mt-msg-burst4">
          <rect x="42" y="48" width="18" height="9" rx="3" fill="color-mix(in srgb, currentColor 72%, transparent)" stroke="currentColor" strokeWidth="1.2" />
        </g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 96 84" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="48" cy="38" r="22" stroke="currentColor" strokeWidth="1.8" opacity=".55" />
      <ellipse cx="48" cy="38" rx="11" ry="22" stroke="currentColor" strokeWidth="1.2" opacity=".4" />
      <line x1="26" y1="38" x2="70" y2="38" stroke="currentColor" strokeWidth="1.2" opacity=".4" />
      <circle className="mt-globe-spin" cx="70" cy="38" r="3.5" fill="color-mix(in srgb, currentColor 85%, transparent)" stroke="currentColor" strokeWidth="1.5" />
      <text className="mt-text-out" x="48" y="30" textAnchor="middle" fontFamily="inherit" fontWeight="700" fontSize="13" fill="currentColor">
        Hello
      </text>
      <text className="mt-text-in" x="48" y="30" textAnchor="middle" fontFamily="inherit" fontWeight="700" fontSize="13" fill="currentColor">
        नमस्ते
      </text>
      <text className="mt-text-out mt-text-out--d" x="48" y="50" textAnchor="middle" fontFamily="inherit" fontSize="10" fill="currentColor" opacity=".7">
        Welcome
      </text>
      <text className="mt-text-in mt-text-in--d" x="48" y="50" textAnchor="middle" fontFamily="inherit" fontSize="10" fill="currentColor" opacity=".7">
        स्वागत है
      </text>
    </svg>
  );
}

export default function MarketingPipeline() {
  const pipelineRef = useRef<HTMLDivElement>(null);
  const packetRef = useRef<HTMLDivElement>(null);
  const boxRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const STEP_DURATION = 2200;
    const FLY_DURATION = 600;
    const pipeline = pipelineRef.current;
    const packet = packetRef.current;
    if (!pipeline || !packet) return;

    let step = 0;
    let cancelled = false;
    const timers: number[] = [];

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    const getCenter = (box: HTMLDivElement) => {
      const pr = pipeline.getBoundingClientRect();
      const br = box.getBoundingClientRect();
      return {
        x: br.left - pr.left + br.width / 2,
        y: br.top - pr.top + br.height / 2,
      };
    };

    const placeAt = (box: HTMLDivElement) => {
      const { x, y } = getCenter(box);
      packet.style.transition = "none";
      packet.style.left = `${x - 16}px`;
      packet.style.top = `${y - 10}px`;
      packet.style.opacity = "0";
    };

    const flyTo = async (box: HTMLDivElement) => {
      const { x, y } = getCenter(box);
      packet.style.opacity = "1";
      packet.style.transition = `left ${FLY_DURATION}ms cubic-bezier(.4,0,.2,1), top ${FLY_DURATION}ms cubic-bezier(.4,0,.2,1)`;
      packet.style.left = `${x - 16}px`;
      packet.style.top = `${y - 10}px`;
      await wait(FLY_DURATION);
    };

    const hidePacket = async () => {
      packet.style.transition = "opacity 0.2s";
      packet.style.opacity = "0";
      await wait(220);
    };

    const run = async () => {
      await wait(400);
      while (!cancelled) {
        const current = step % 4;
        setActiveIndex(current);
        await wait(STEP_DURATION);
        if (cancelled) break;

        if (current === 3) {
          setActiveIndex(-1);
          await wait(300);
          step += 1;
          continue;
        }

        const from = boxRefs.current[current];
        const to = boxRefs.current[current + 1];
        if (!from || !to) {
          step += 1;
          continue;
        }

        placeAt(from);
        await wait(32);
        if (cancelled) break;
        await flyTo(to);
        if (cancelled) break;
        await hidePacket();
        step += 1;
      }
    };

    void run();

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return (
    <div className="mt-pipeline-wrap">
      <p className="mt-pipeline-label">How it works your full marketing workflow</p>
      <div className="mt-pipeline" id="pipeline" ref={pipelineRef}>
        {moreToolsPipeline.map((item, index) => (
          <div key={item.id} className="contents">
            <div
              ref={(el) => {
                boxRefs.current[index] = el;
              }}
              className={`mt-pipe-box${activeIndex === index ? " active" : ""}`}
              id={item.id}
            >
              <span className="mt-pipe-tag">{item.tag}</span>
              <span className="mt-pipe-step">{item.step}</span>
              <div className="mt-pipe-canvas">
                <PipelineCanvas kind={item.kind} />
              </div>
              <span className="mt-pipe-name">{item.name}</span>
              <span className="mt-pipe-desc">{item.desc}</span>
            </div>
            {index < moreToolsPipeline.length - 1 ? <PipeArrow /> : null}
          </div>
        ))}
        <div className="mt-packet" ref={packetRef} aria-hidden>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}
